/* eslint-disable @next/next/no-img-element -- Google's own official badge
   images (light variants, from Google's badge pack), served from public/. */
import { PreferredSourceLink } from "@/components/chrome/preferred-source-link"

/**
 * GooglePreferredButton — THE two Google badges, exactly as the owner spec'd
 * (FRIDGE-608 + his 08/28 correction: "i told you which 2 types, what size,
 * what style, and where. side by side under the follow us buttons").
 *
 * The preferred-source button renders in Google's badge STYLE but as HTML
 * (FRIDGE-691, owner 08/31/2026: it "needs to say -- Add Fridge.com as a
 * preferred source on Google" — wording Google's fixed badge PNG cannot
 * carry). The News badge stays GOOGLE'S OWN official LIGHT image (his
 * not-black ruling): "FOLLOW ON Google News" — 468×128 asset → renders
 * 183×50. Side by side, centered when `center`, both at 50px height.
 *
 * The News badge renders ONLY when a Google News publication URL exists
 * (GOOGLE_NEWS_PUBLICATION_URL below). No Fridge.com publication exists yet —
 * creating one is Google Publisher Center account work only the owner can do
 * (Owner Blocked); the badge appears the moment the constant gets the URL.
 * One preferred-sources control: PreferredSourceLink (lib/preferred-source.ts
 * holds the URL and Google's own popup mechanics) — never a second copy.
 * FRIDGE-876, owner 09/05/2026: the button opens a centered popup and the
 * reader's current page never navigates away.
 */

/**
 * Set to the news.google.com/publications/<id> URL when the owner creates it.
 * Live-checked 08/29/2026: Google News has NO fridge.com source entity —
 * searches for "Fridge.com" return third-party coverage only (The National
 * Law Review carrying the How America Refrigerates report) polluted by
 * nukethefridge.com, so no search URL is an acceptable stand-in. The badge
 * order when both render: preferred-source LEFT, News follow RIGHT (owner,
 * 08/29/2026).
 */
export const GOOGLE_NEWS_PUBLICATION_URL: string | null = null

export function GooglePreferredButton({
  className = "",
  center = false,
}: {
  className?: string
  center?: boolean
}) {
  // Owner 09/06/2026: on articles the button sits inside `.blog-content`, whose
  // `a { text-decoration: underline }` rule reached it — a button never carries
  // a link underline. The important utilities outrank that class rule.
  return (
    <div className={`flex flex-wrap items-center gap-3 ${center ? "justify-center" : ""} ${className}`}>
      {/* FRIDGE-691 (owner, 08/31/2026): the button "needs to say -- Add
          Fridge.com as a preferred source on Google". Google's official badge
          PNG bakes its own fixed wording ("Add as a preferred source on
          Google") into the image, so complying means rendering the badge as
          HTML in the badge's exact style — same white pill, same border, same
          50px height, the official multicolor G — with the owner's wording as
          real text (a bonus: it is now readable by crawlers and screen
          readers, and crisp at every DPI). */}
      <PreferredSourceLink className="inline-flex h-[50px] items-center gap-2.5 rounded-lg border border-gray-300 bg-white px-3.5 no-underline! hover:no-underline! focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff9951] focus-visible:ring-offset-2">
        <img
          src="/icons/social/google.svg"
          alt=""
          aria-hidden
          width={24}
          height={24}
          // FRIDGE-1505 (I2): a CSS size too; with height left auto (Tailwind's
          // preflight) Chromium flagged this lazy icon on every page (322).
          className="h-6 w-6 shrink-0"
          loading="lazy"
        />
        <span className="text-left text-[13px] font-medium leading-snug text-gray-900 no-underline!">
          Add Fridge.com as a preferred
          <br />
          source on Google
        </span>
      </PreferredSourceLink>
      {GOOGLE_NEWS_PUBLICATION_URL && (
        <a
          href={GOOGLE_NEWS_PUBLICATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff9951] focus-visible:ring-offset-2"
        >
          <img
            src="/badges/google-news-follow.png"
            alt="Follow Fridge.com on Google News"
            width={183}
            height={50}
            style={{ height: 50, width: "auto" }}
            loading="lazy"
          />
        </a>
      )}
    </div>
  )
}
